import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useAuth,
} from './AuthContext';

import {
  db,
  supabase,
} from '../services/supabase';


const FamilyStatusContext =
  createContext(null);


export const useFamilyStatus =
  () => {

    const context =
      useContext(
        FamilyStatusContext
      );


    if (!context) {
      throw new Error(
        'useFamilyStatus must be used within a FamilyStatusProvider'
      );
    }


    return context;
  };


export const FamilyStatusProvider = ({
  children,
}) => {

  const {
    parent,
    refreshParent,
  } = useAuth();


  /*
   * ---------------------------------------------------------
   * FAMILY LIFECYCLE STATE
   * ---------------------------------------------------------
   *
   * AuthContext already loads the parent's related family row,
   * so there is no need for a second family query here.
   */

  const family =
    parent
      ?.families_1740480000000 ||
    null;


  const status =
    family?.status ||
    'active';


  const isFamilyInactive =
    status !== 'active';


  const deactivatedAt =
    family
      ?.deactivated_at ||
    null;


  const deletionScheduledFor =
    family
      ?.deletion_scheduled_for ||
    null;

  const isAdminDeletionScheduled =
    Boolean(family?.admin_deletion_requested_by);


  /*
   * ---------------------------------------------------------
   * SUBSCRIPTION / ENTITLEMENT ACCESS STATE
   * ---------------------------------------------------------
   *
   * This is intentionally separate from the family lifecycle.
   * A family may be lifecycle-active while its subscription is
   * expired and therefore read-only. Supabase remains the source
   * of truth through resolve_my_family_access_state().
   */

  const [
    familyAccessState,
    setFamilyAccessState,
  ] = useState(null);


  const [
    familyAccessLoading,
    setFamilyAccessLoading,
  ] = useState(false);


  const [
    familyAccessError,
    setFamilyAccessError,
  ] = useState(null);


  const loadFamilyAccessState =
    useCallback(
      async () => {
        if (!parent?.family_id) {
          setFamilyAccessState(null);
          setFamilyAccessError(null);
          setFamilyAccessLoading(false);
          return null;
        }

        try {
          setFamilyAccessLoading(true);
          setFamilyAccessError(null);

          const accessState =
            await db.resolveMyFamilyAccessState();

          setFamilyAccessState(accessState);
          return accessState;
        } catch (error) {
          console.error(
            'Unable to load family access state:',
            error
          );

          setFamilyAccessState(null);
          setFamilyAccessError(error);
          return null;
        } finally {
          setFamilyAccessLoading(false);
        }
      },
      [
        parent?.family_id,
      ]
    );


  useEffect(() => {
    loadFamilyAccessState();
  }, [
    loadFamilyAccessState,
  ]);


  /*
   * ---------------------------------------------------------
   * FAMILY OWNER NAME
   * ---------------------------------------------------------
   *
   * Owners can use their own parent display name directly.
   * Guardians need to read the family adult list so the shared
   * banner can show the actual Family Owner's name.
   */

  const [
    ownerName,
    setOwnerName,
  ] = useState('');


  const [
    ownerLoading,
    setOwnerLoading,
  ] = useState(false);


  const loadOwnerName =
    useCallback(
      async () => {

        if (
          !parent?.family_id
        ) {
          setOwnerName('');
          return;
        }


        if (
          parent?.role ===
          'owner'
        ) {
          setOwnerName(
            parent?.name ||
              'Family Owner'
          );

          return;
        }


        try {

          setOwnerLoading(
            true
          );


          const {
            data,
            error,
          } =
            await supabase.rpc(
              'get_my_family_adults'
            );


          if (error) {
            throw error;
          }


          const adults =
            Array.isArray(data)
              ? data
              : [];


          const owner =
            adults.find(
              (adult) =>
                adult
                  ?.member_role ===
                'owner'
            );


          setOwnerName(
            owner?.display_name ||
              owner?.name ||
              'Family Owner'
          );

        } catch (error) {

          console.error(
            'Unable to load Family Owner name:',
            error
          );


          setOwnerName(
            'Family Owner'
          );

        } finally {

          setOwnerLoading(
            false
          );
        }
      },
      [
        parent?.family_id,
        parent?.name,
        parent?.role,
      ]
    );


  useEffect(() => {

    loadOwnerName();

  }, [
    loadOwnerName,
  ]);


  /*
   * ---------------------------------------------------------
   * REFRESH FAMILY STATUS
   * ---------------------------------------------------------
   *
   * Refresh lifecycle data and entitlement data together so the
   * UI immediately reflects recovery or future billing updates.
   */

  const refreshFamilyStatus =
    useCallback(
      async () => {

        await refreshParent({
          blockUI: false,
        });

        await loadFamilyAccessState();

      },
      [
        refreshParent,
        loadFamilyAccessState,
      ]
    );


  const value =
    useMemo(
      () => ({
        family,

        status,
        isFamilyInactive,

        ownerName,
        ownerLoading,

        deactivatedAt,
        deletionScheduledFor,
        isAdminDeletionScheduled,

        familyAccessState,
        familyAccessLoading,
        familyAccessError,

        plan:
          familyAccessState?.plan || null,
        entitlementStatus:
          familyAccessState?.entitlement_status || null,
        effectiveStatus:
          familyAccessState?.effective_status || null,
        canRead:
          familyAccessState?.can_read ?? null,
        canWrite:
          familyAccessState?.can_write ?? null,
        activeChildLimit:
          familyAccessState?.active_child_limit ?? null,
        trialEndsAt:
          familyAccessState?.trial_ends_at || null,
        currentPeriodEndsAt:
          familyAccessState?.current_period_ends_at || null,
        graceEndsAt:
          familyAccessState?.grace_ends_at || null,
        accessEndsAt:
          familyAccessState?.access_ends_at || null,

        refreshFamilyAccessState:
          loadFamilyAccessState,
        refreshFamilyStatus,
      }),
      [
        family,
        status,
        isFamilyInactive,
        ownerName,
        ownerLoading,
        deactivatedAt,
        deletionScheduledFor,
        isAdminDeletionScheduled,
        familyAccessState,
        familyAccessLoading,
        familyAccessError,
        loadFamilyAccessState,
        refreshFamilyStatus,
      ]
    );


  return (
    <FamilyStatusContext.Provider
      value={
        value
      }
    >
      {children}
    </FamilyStatusContext.Provider>
  );
};
